import {
  InstantAnswerBuilderFn,
  InstantAnswerContext,
  InstantAnswerScenario,
} from "@faire/help-center-self-serve/types";
import { StrictLocalizeFunction } from "@faire/web--source/common/localization";

import {
  MessageRetailerOrBrandButton,
  additionalDataRequiredForContactingRetailerOrBrandCTA,
} from "../../shared/instantAnswerUtils";
import { getDeliveredAtDateFromIAContext } from "../../utils/getDeliveredAtDateFromIAContext";
import { orderCanBeReportedBasedOnDeliveryDate } from "../../utils/orderCanBeReportedBasedOnDeliveryDate";

import {
  DO_NOT_PROCESS_DAMAGED_AS_RETURN,
  getReportItemsOrRequestExtensionCTA,
} from "./shared";

export const ReportDamagedMissingItemsOrShipmentsIA: InstantAnswerBuilderFn = (
  strictLocalize
) => {
  return {
    titleBuilder: (context) => {
      return getAnswer(context, strictLocalize).getTitle();
    },

    descriptionBuilder: (context) => {
      return getAnswer(context, strictLocalize).getDescription();
    },

    primaryCtaBuilder: {
      textBuilder: (context) => {
        return getReportItemsOrRequestExtensionCTA(context, strictLocalize)
          ?.label;
      },
      linkBuilder: (context) => {
        return getReportItemsOrRequestExtensionCTA(
          context,
          strictLocalize
        )?.getLink?.(context.brandOrder);
      },
    },
    secondaryCtaBuilder: {
      textBuilder: (context) => {
        return MessageRetailerOrBrandButton(context, strictLocalize)?.label;
      },
      onClickFunctionBuilder: (context) => {
        return MessageRetailerOrBrandButton(context, strictLocalize)?.onClick;
      },
    },

    additionalRequiredData:
      additionalDataRequiredForContactingRetailerOrBrandCTA(),
  };
};

const getAnswer = (
  context: InstantAnswerContext,
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => {
  const { brandOrder, shippingContext } = context;
  const deliveredAt = getDeliveredAtDateFromIAContext(context);
  if (!brandOrder || !shippingContext || !deliveredAt) {
    return NO_ORDER_OR_DELIVERY_AVAILABLE_CASE(strictLocalize);
  }

  if (orderCanBeReportedBasedOnDeliveryDate(deliveredAt)) {
    return REPORT_PERIOD_IS_ACTIVE_CASE(strictLocalize);
  }

  return REPORT_PERIOD_IS_OVER_CASE(strictLocalize);
};

const NO_ORDER_OR_DELIVERY_AVAILABLE_CASE = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => ({
  getTitle: () => REPORTING_DAMAGED_OR_MISSING_ITEMS(strictLocalize),
  getDescription: () => [
    REPORTING_DAMAGED_OR_MISSING_ITEMS_FULL_EXPLANATION(strictLocalize),
    DO_NOT_PROCESS_DAMAGED_AS_RETURN(strictLocalize),
  ],
});

const REPORTING_DAMAGED_OR_MISSING_ITEMS = (
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize({
    defaultMessage: "Reporting damaged or missing items",
    description: {
      text: "Title for an answer about reporting damaged or missing items",
    },
  });

const REPORTING_DAMAGED_OR_MISSING_ITEMS_FULL_EXPLANATION = (
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize({
    defaultMessage:
      "If the order was delivered less than 14 days ago, you can message the brand directly about your damaged or missing items or file a damaged or missing item report using the button below. If the order was delivered more than 14 days ago, your window to report the items has passed. You may request an extension if there are exceptional circumstances.",
    description: {
      text: "Description for an answer about reporting damaged or missing items",
    },
  });

const REPORT_PERIOD_IS_ACTIVE_CASE = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => ({
  getTitle: () => REPORTING_DAMAGED_OR_MISSING_ITEMS(strictLocalize),
  getDescription: () => [MESSAGE_BRAND(strictLocalize)],
});

const MESSAGE_BRAND = (strictLocalize: StrictLocalizeFunction) =>
  strictLocalize({
    defaultMessage:
      "You can message the brand directly about your damaged or missing items or file a damaged or missing item report using the button below.",
    description: {
      text: "Description for an answer about reporting damaged or missing items",
    },
  });

const REPORT_PERIOD_IS_OVER_CASE = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => ({
  getTitle: () => REPORT_PERIOD_IS_OVER(strictLocalize),
  getDescription: () => [
    MESSAGE_BRAND_TO_LEARN_MORE(strictLocalize),
    DO_NOT_PROCESS_DAMAGED_AS_RETURN(strictLocalize),
  ],
});

const REPORT_PERIOD_IS_OVER = (strictLocalize: StrictLocalizeFunction) =>
  strictLocalize({
    defaultMessage:
      "Your 14-day period to report damaged or missing items for this order has passed.",
    description: {
      text: "Title for an answer about reporting damaged or missing items",
    },
  });

const MESSAGE_BRAND_TO_LEARN_MORE = (strictLocalize: StrictLocalizeFunction) =>
  strictLocalize({
    defaultMessage:
      "You may message the brand to learn more about the damaged or missing items or request an extension if there are exceptional circumstances.",
    description: {
      text: "Description for an answer about reporting damaged or missing items",
    },
  });
